import React, { useState } from 'react';

function ContributionForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    heardAbout: '',
    volunteerTime: false,
    activelyInvolved: false,
    networkResource: false,
    donateMoney: false,
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.name || !formData.email || !formData.phone) {
      setError('Please fill in all required fields (name, email, phone).');
      setLoading(false);
      return;
    }

    if (
      !formData.volunteerTime &&
      !formData.activelyInvolved &&
      !formData.networkResource &&
      !formData.donateMoney
    ) {
      setError('Please select at least one way you want to contribute.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/.netlify/functions/submitForm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        heardAbout: '',
        volunteerTime: false,
        activelyInvolved: false,
        networkResource: false,
        donateMoney: false,
        message: '',
      });

      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError('An error occurred. Please try again or contact us directly.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="form-section">
      <div className="container">
        <h2>How Would You Like to Be Part of This?</h2>

        <div className="foundation-clarity">
          <p>
            The TMT Learning Center is the flagship initiative of <strong>Plus One Foundation</strong>,
            a Section 8 not-for-profit organization built on a simple idea: each generation does not start from zero.
            We take what our teachers gave us, improve it slightly through our own experience, and pass it
            forward, carefully, responsibly, and in living form.
          </p>
          <p>
            The Learning Center is where we're starting. But <strong>Plus One Foundation</strong> is an umbrella for
            multiple initiatives. We're developing Science Lab Kits (a TMT favourite), exploring other projects, and
            we're open to initiatives you bring to our attention, anything that passes forward the same principle:
            learning fluency as a transferable skill.
          </p>
          <p>
            When you contribute, whether your time, ideas, network, or resources, you're investing in this entire
            ecosystem, not just one program.
          </p>
        </div>

        <div className="form-container">
          {submitted && (
            <div className="success-message">
              Thank you for your interest! We'll contact you within 5 days to discuss 
              how you can support this work.
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            {/* Basic Info */}
            <div className="form-group">
              <label htmlFor="name">
                Full Name <span style={{ color: '#D4A574' }}>*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email <span style={{ color: '#D4A574' }}>*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">
                Phone <span style={{ color: '#D4A574' }}>*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">City / Location</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="heardAbout">How did you hear about us?</label>
              <input
                type="text"
                id="heardAbout"
                name="heardAbout"
                value={formData.heardAbout}
                onChange={handleChange}
              />
            </div>

            {/* Contribution Options */}
            <div className="form-group">
              <label>How would you like to contribute?</label>
              <div className="contribution-options">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="volunteerTime"
                    name="volunteerTime"
                    checked={formData.volunteerTime}
                    onChange={handleChange}
                  />
                  <label htmlFor="volunteerTime">I'm willing to give my time</label>
                </div>

                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="activelyInvolved"
                    name="activelyInvolved"
                    checked={formData.activelyInvolved}
                    onChange={handleChange}
                  />
                  <label htmlFor="activelyInvolved">
                    I have ideas and want to get more actively involved
                  </label>
                </div>

                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="networkResource"
                    name="networkResource"
                    checked={formData.networkResource}
                    onChange={handleChange}
                  />
                  <label htmlFor="networkResource">
                    I can be a bridge: connections, resources, partnerships
                  </label>
                </div>

                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="donateMoney"
                    name="donateMoney"
                    checked={formData.donateMoney}
                    onChange={handleChange}
                  />
                  <label htmlFor="donateMoney">I want to donate</label>
                </div>
              </div>
            </div>

            {/* Optional Message */}
            <div className="form-group">
              <label htmlFor="message">
                Are there any specific, unique ways in which the learning center 
                excites you and the way you want to get involved?
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="(Optional)"
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default ContributionForm;